/**
 * ClientContextTile — unified client + policies-in-force card.
 *
 * Shared across three surfaces:
 *   ora WorkflowDetailDrawer  (historic mode — fed by Remix useFetcher)
 *   Plugin InstanceDetailDrawer (historic mode — fed by orchestratorApi)
 *   Plugin LaunchTab (live mode — fed by useClientContext state machine)
 *
 * Props are a discriminated union; the caller maps its local state once
 * and passes the result — no conditional clutter at the call site.
 *
 * NOTE — Tailwind tokens:
 *   animate-client-ctx-fade-in / animate-client-ctx-pulse — defined in
 *   app/common/workflows/tailwind-preset.ts, consumed by both apps.
 *   see-possible-tile-shadow — @layer utility in each app's CSS entry file:
 *     ora:    app/tailwind.css (line 632)
 *     plugin: apps/outlook-addin/src/styles.css (line 21)
 *   primary-* color tokens — defined in each app's tailwind.config.ts.
 */
import { useEffect, useState, type ReactNode } from "react";
import type {
  ClientDetails,
  ClientSearchResult,
  Opportunity,
  PolicyInForce,
} from "~/common/workflows/types/client";
import CustomTooltip from "~/common/components/CustomTooltip";
import Skeleton from "~/common/workflows/components/Skeleton";
import { ClientSearchField } from "~/common/workflows/components/ClientSearchField";
import { ClientSuggestionRow } from "~/common/workflows/components/ClientSuggestionRow";

// ── Prop types ────────────────────────────────────────────────────────────────

export interface SearchAffordance {
  /** Pre-populated suggestions (e.g. from analyze_email ranked results). */
  suggestedResults?: readonly ClientSearchResult[];
  /** ora's client search (ClientSearchField). Without it the tile offers no
   *  search — the old plain-input fallback is gone (story 28633). */
  searchClients?: (query: string) => Promise<ClientSearchResult[]>;
  onSelectClient?: (clientSk: number) => void;
  /** Full result callback — used when caller needs more than just the sk. */
  onSelectClientResult?: (result: ClientSearchResult) => void;
}

export type ClientContextTileProps =
  | { kind: "hidden" }
  | { kind: "loading" }
  | { kind: "error"; message?: string }
  | ({
      kind: "not_found";
      /** When true, tile is in "no email selected" empty state. */
      noEmailSelected?: boolean;
      onReset?: () => void;
    } & SearchAffordance)
  | {
      kind: "loaded";
      details: ClientDetails;
      /** X button — pass undefined on read-only drawer surfaces. */
      onReset?: () => void;
      /** When true, policy fetch failed — renders "unavailable" note. */
      policiesUnavailable?: boolean;
      /** Collapse Policies in force behind a chevron (launch form). */
      collapsiblePolicies?: boolean;
      /** A control on the client-name row, right-aligned (the launch form's
       *  "Change" link), so it costs no row of its own. */
      headerAction?: ReactNode;
      /** Drop the tile's own bottom margin — for a host that spaces its
       *  children with a gap (the launch form). */
      flush?: boolean;
    };

// ── Helpers ───────────────────────────────────────────────────────────────────

export function expiryDisplay(dateStr: string): { label: string; tone: string } {
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { label: "expired", tone: "text-primary-error-600" };
  if (days <= 30) return { label: `${days}d`, tone: "text-primary-error-600" };
  if (days <= 60) return { label: `${days}d`, tone: "text-primary-warning-500" };
  return { label: dateStr.slice(0, 10), tone: "text-primary-gray-light-400" };
}

export function likelihoodTone(l: string): string {
  if (l === "High") return "text-primary-success-600";
  if (l === "Medium") return "text-primary-warning-500";
  if (l === "Low") return "text-primary-error-600";
  return "text-primary-gray-light-400";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ClientContextTile(props: ClientContextTileProps): JSX.Element | null {
  if (props.kind === "hidden") return null;

  if (props.kind === "loading") {
    return (
      <div className="mb-3" data-testid="client-context-tile-loading">
        <Skeleton className="h-[68px] w-full" />
      </div>
    );
  }

  if (props.kind === "error") {
    return (
      <div
        className="mb-3 rounded-lg border border-primary-error-100 bg-primary-error-25 px-3 py-2 text-xs text-primary-error-600"
        data-testid="client-context-tile-error"
      >
        {props.message ?? "Client details unavailable."}
      </div>
    );
  }

  if (props.kind === "not_found") {
    return (
      <NotFoundSection
        suggestedResults={props.suggestedResults}
        searchClients={props.searchClients}
        onSelectClient={props.onSelectClient}
        onSelectClientResult={props.onSelectClientResult}
        noEmailSelected={props.noEmailSelected}
      />
    );
  }

  // kind === "loaded"
  return <LoadedClientTile {...props} />;
}

function LoadedClientTile({
  details,
  onReset,
  policiesUnavailable = false,
  collapsiblePolicies = false,
  headerAction,
  flush = false,
}: Extract<ClientContextTileProps, { kind: "loaded" }>) {
  const [pifOpen, setPifOpen] = useState(!collapsiblePolicies);
  const { client } = details;
  // policies_in_force and opportunities are typed non-null, but the API can
  // send null for either — guard both identically (a null opportunities was
  // crashing the loaded tile at opportunities.length).
  const activePolicies = (details.policies_in_force as PolicyInForce[] | undefined ?? []).filter(
    (p) => p.status === "active",
  );
  const opportunities = (details.opportunities as Opportunity[] | undefined) ?? [];
  const locationParts = [client.city, client.state].filter(Boolean).join(", ");
  const totalPremium = activePolicies.reduce((sum, p) => sum + (p.expiring_premium ?? 0), 0);

  return (
    <div
      className={`${flush ? "" : "mb-3 "}animate-client-ctx-fade-in overflow-hidden rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-25`}
      data-testid="client-context-tile-loaded"
      aria-live="polite"
    >
      {/* Row 1 — status dot + client name + close */}
      <div className="flex items-center justify-between px-3 pb-[3px] pt-2">
        <div className="flex min-w-0 items-center gap-[6px]">
          <div className="h-[7px] w-[7px] flex-shrink-0 rounded-full bg-primary-success-600" />
          <span className="truncate text-xs font-semibold text-primary-gray-light-900">
            {client.client_name}
          </span>
        </div>
        {headerAction}
        {onReset && (
          // ora's tooltip, not a native title (story 28633); the button's own
          // aria-label stays its name.
          <CustomTooltip
            id="client-context-reset"
            title="Search for a different client"
            placement="top"
            arrow
            isOnModal
          >
            <button
              type="button"
              onClick={onReset}
              className="flex touch-target touch-target-w items-center justify-center border-none bg-transparent p-0 text-sm leading-none text-primary-gray-light-400 hover:text-primary-gray-light-600"
              aria-label="Reset client selection"
            >
              ✕
            </button>
          </CustomTooltip>
        )}
      </div>

      {/* Row 2 — location · branch + policy count · opp count */}
      <div className="flex flex-col gap-0.5 px-3 pb-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <span className="min-w-0 truncate text-xs text-primary-gray-light-500">
          {locationParts}
          {client.branch_name ? ` · ${client.branch_name}` : ""}
        </span>
        <span className="flex-shrink-0 text-xs text-primary-gray-light-500">
          {activePolicies.length} {activePolicies.length === 1 ? "policy" : "policies"}
          {opportunities.length > 0 && ` · ${opportunities.length} opp`}
        </span>
      </div>

      {/* Policies-in-force section */}
      {(policiesUnavailable || activePolicies.length > 0) && (
        <>
          {collapsiblePolicies ? (
            <button
              type="button"
              onClick={() => setPifOpen((open) => !open)}
              aria-expanded={pifOpen}
              data-testid="client-pif-toggle"
              className="flex touch-target w-full items-center gap-2 border-t border-primary-gray-light-200 px-3 py-[6px] text-left"
            >
              <span
                aria-hidden="true"
                className="flex-shrink-0 text-primary-gray-light-400"
                style={{ transform: pifOpen ? "rotate(90deg)" : undefined }}
              >
                ▸
              </span>
              <span className="min-w-0 flex-grow truncate text-2xs font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                Policies in force
              </span>
              {totalPremium > 0 && (
                <span className="flex-shrink-0 text-2xs font-semibold text-primary-gray-light-500">
                  ${totalPremium.toLocaleString(undefined, { maximumFractionDigits: 0 })} total
                </span>
              )}
            </button>
          ) : (
            <div className="flex items-center justify-between gap-2 border-t border-primary-gray-light-200 px-3 py-[6px]">
              <span className="min-w-0 truncate text-2xs font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                Policies in force
              </span>
              {totalPremium > 0 && (
                <span className="flex-shrink-0 text-2xs font-semibold text-primary-gray-light-500">
                  ${totalPremium.toLocaleString(undefined, { maximumFractionDigits: 0 })} total
                </span>
              )}
            </div>
          )}

          {pifOpen && (policiesUnavailable ? (
            <div
              className="px-3 pb-[10px] text-xs text-primary-gray-light-400"
              data-testid="client-context-tile-no-policies"
            >
              Policy details unavailable for this client.
            </div>
          ) : (
            <div className="flex flex-col gap-1 px-3 pb-[10px]">
              {activePolicies.map((p, i) => {
                const exp = p.expiration_date ? expiryDisplay(p.expiration_date) : null;
                return (
                  <div
                    key={`${p.policy_line_sk ?? "null"}-${i}`}
                    className="flex items-center justify-between text-xs"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-1">
                      <span className="min-w-0 truncate font-medium text-primary-gray-light-900">
                        {p.policy_type}
                      </span>
                      {p.expiring_premium != null && (
                        <span className="flex-shrink-0 text-xs text-primary-gray-light-500">
                          · ${p.expiring_premium.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      )}
                      <span className={`flex-shrink-0 text-2xs font-semibold ${likelihoodTone(p.renewal_likelihood)}`}>
                        {p.renewal_likelihood}
                      </span>
                    </div>
                    {exp && (
                      <div className={`ml-2 flex-shrink-0 text-xs ${exp.tone}`}>
                        exp {exp.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}

      {/* Opportunities — omitted on the launch card (collapsiblePolicies). */}
      {!collapsiblePolicies && opportunities.length > 0 && (
        <div className="border-t border-primary-gray-light-200 px-3 pb-[10px] pt-[6px]">
          <div className="mb-[6px] text-2xs font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
            Opportunities
          </div>
          <div className="flex flex-col gap-1">
            {opportunities.map((o) => (
              <div
                key={o.opportunityId}
                className="flex items-start justify-between text-xs"
              >
                <div className="min-w-0 flex-1">
                  <span className="break-words font-medium text-primary-gray-light-900">{o.policyType}</span>
                  {o.reasoning && (
                    <div className="mt-[1px] break-words text-xs text-primary-gray-light-500">
                      {o.reasoning}
                    </div>
                  )}
                </div>
                {o.estimatedPremium != null && (
                  <span className="ml-2 flex-shrink-0 text-xs font-semibold text-primary-success-600">
                    ~${o.estimatedPremium.toLocaleString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Not-found / search affordance ─────────────────────────────────────────────

function NotFoundSection({
  suggestedResults = [],
  searchClients,
  onSelectClient,
  onSelectClientResult,
  noEmailSelected = false,
}: SearchAffordance & { noEmailSelected?: boolean }) {
  // Suggestions-first UX: when analyze_email returned candidates we show ONLY
  // the "did you mean" list with a dismiss control; dismissing collapses to the
  // manual search box (mutually exclusive — never both at once). Reset the
  // dismissal whenever a fresh suggestion set arrives so a new ambiguous email
  // re-shows its candidates even if the previous list was dismissed.
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  useEffect(() => {
    setSuggestionsDismissed(false);
  }, [suggestedResults]);

  // ora's ONE client search control (story 28633), compact and in-page. The
  // workflow-only plain input that stood in when a host passed no
  // `searchClients` is gone: every host passes one.
  const search = searchClients ? (
    <ClientSearchField
      id="client-context-tile-search"
      searchClients={searchClients}
      onPick={(result) => {
        if (onSelectClientResult) {
          onSelectClientResult(result);
        } else {
          onSelectClient?.(result.clientSk);
        }
      }}
    />
  ) : null;

  if (noEmailSelected && suggestedResults.length === 0) {
    return (
      <div
        className="mb-3 flex flex-col gap-2 rounded-lg border border-dashed border-primary-gray-light-200 bg-primary-gray-light-25 p-3 text-center text-xs text-primary-gray-light-500"
        data-testid="client-context-tile-no-email"
      >
        Open an email to identify a client, or search by name.
        {search}
      </div>
    );
  }

  const showSuggestions = suggestedResults.length > 0 && !suggestionsDismissed;

  return (
    <div className="mb-3" data-testid="client-context-tile-not-found">
      {showSuggestions ? (
        // Suggestions-first: show the candidate list + a dismiss control. The
        // manual search box is hidden until the operator dismisses the list.
        <div className="mb-2" data-testid="client-suggestions">
          <div className="mb-1 text-xs text-primary-gray-light-400">
            {(suggestedResults[0] as { score?: number }).score != null
              ? "Select a client:"
              : "Could not identify client — did you mean:"}
          </div>
          <div className="overflow-hidden rounded-md border border-primary-gray-light-200">
            {suggestedResults.map((r, i) => (
              <ClientSuggestionRow
                key={r.clientSk}
                result={r}
                onPick={(picked) => onSelectClient?.(picked.clientSk)}
                last={i === suggestedResults.length - 1}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSuggestionsDismissed(true)}
            data-testid="client-suggestions-dismiss"
            className="mt-1 inline-flex touch-target items-center text-xs text-primary-gray-light-400 underline hover:text-primary-gray-light-600"
          >
            None of these — search manually
          </button>
        </div>
      ) : (
        search
      )}
    </div>
  );
}

export default ClientContextTile;
